/**
 * Pemilihan organisasi setelah login — MURNI.
 *
 * Setiap endpoint Workforce menuntut `x-organization-id`; bahkan `/me` tidak dapat dipanggil
 * tanpanya. Jadi login saja belum cukup untuk masuk — klien harus tahu organisasi mana yang
 * dipakai SEBELUM permintaan pertama. Daftar itu datang dari HBS (`GET /me/memberships`), sumber
 * kebenaran keanggotaan yang sama yang dipakai server Workforce untuk memverifikasinya.
 *
 * Modul ini memutuskan apa yang terjadi setelah daftar itu tiba. Dipisahkan dari komponen agar
 * dapat diuji: ketiga cabangnya punya perilaku yang berbeda, dan yang paling mudah salah justru
 * cabang yang paling jarang terlihat oleh penulisnya (daftar kosong).
 */

export interface Membership {
  readonly organizationId: string;
  readonly organizationName: string;
}

export type MembershipChoice =
  /**
   * Login berhasil, tetapi pengguna bukan anggota organisasi mana pun yang aktif.
   *
   * Ini BUKAN kegagalan login, dan tidak boleh ditampilkan sebagai "email atau kata sandi salah".
   * Orangnya terbukti ada; yang belum ada adalah aksesnya. Menyamakan keduanya membuat pengguna
   * mencoba ulang kata sandi yang sebenarnya sudah benar, dan membuat admin mencari masalah di
   * tempat yang salah.
   */
  | { readonly kind: 'none' }
  /** Tepat satu organisasi — tidak ada yang perlu dipilih, langsung masuk. */
  | { readonly kind: 'single'; readonly membership: Membership }
  /** Lebih dari satu — pengguna memilih. `preselected` dipakai bila pilihan terakhir masih sah. */
  | {
      readonly kind: 'choose';
      readonly options: readonly Membership[];
      readonly preselected: Membership | null;
    };

/**
 * Menentukan langkah berikutnya dari daftar keanggotaan.
 *
 * `rememberedOrganizationId` adalah pilihan terakhir pengguna di perangkat ini. Ia hanya
 * dipakai bila organisasinya MASIH ADA di daftar — keanggotaan dapat dicabut, dan memakai
 * pilihan lama tanpa memeriksanya akan mengirim organisasi yang sudah tidak sah lalu gagal
 * sebagai 403 yang membingungkan di layar yang tidak menjelaskan apa-apa.
 */
export function decideMembership(
  memberships: readonly Membership[],
  rememberedOrganizationId?: string | null,
): MembershipChoice {
  if (memberships.length === 0) return { kind: 'none' };
  if (memberships.length === 1) return { kind: 'single', membership: memberships[0] as Membership };

  const remembered = memberships.find((m) => m.organizationId === rememberedOrganizationId) ?? null;
  return { kind: 'choose', options: memberships, preselected: remembered };
}

/** Kunci penyimpanan pilihan terakhir. Nilai non-rahasia — hanya UUID organisasi. */
export const REMEMBERED_ORG_KEY = 'workforce.organizationId';

/**
 * Membaca daftar keanggotaan dari respons HBS.
 *
 * HBS mengembalikan lebih banyak kolom daripada yang dibutuhkan layar ini (slug, peran, status).
 * Yang diambil hanya dua, dan sengaja: apa pun yang ikut masuk ke state akan ikut terbaca saat
 * seseorang men-debug di browser, dan tidak ada alasan menaruh lebih banyak di sana.
 *
 * Baris tanpa `organizationId` dibuang alih-alih dibiarkan menjadi `undefined` — organisasi tanpa
 * id akan dikirim sebagai header kosong dan ditolak server dengan galat yang tidak menyebut
 * sebabnya.
 */
export function parseMemberships(payload: unknown): readonly Membership[] {
  if (!Array.isArray(payload)) return [];
  return payload.flatMap((row): Membership[] => {
    if (typeof row !== 'object' || row === null) return [];
    const record = row as Record<string, unknown>;
    const organizationId = record.organizationId;
    if (typeof organizationId !== 'string' || organizationId.trim().length === 0) return [];
    const name = record.organizationName;
    return [
      {
        organizationId,
        organizationName: typeof name === 'string' && name.length > 0 ? name : organizationId,
      },
    ];
  });
}
